import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@heroui/react'

import { FormModal } from '@/components/ui/form-modal'
import { FormSection } from '@/components/ui/form-section'
import {
  NumberFieldControl,
  SelectFieldControl,
  TextFieldControl,
} from '@/components/ui/form-fields'
import { errorMessage } from '@/lib/api/api-error'

import { useInventoryCategories, useStockItemActions, useUnitCatalog } from '../hooks'
import { ConstructionType, StockItemType } from '../types'
import type { CreateStockItemBody, StockItem, UpdateStockItemBody } from '../types'
import {
  UNIT_COST_FORMAT,
  itemStatusLabels,
  itemTypeLabels,
  unitOptionGroups,
} from './inventory-format'

/** "No category" as a key the select can hold — an unset select cannot be returned to. */
const NO_CATEGORY = 'none'

const schema = z.object({
  name: z.string().trim().min(1, 'Give the item a name.').max(255, 'At most 255 characters.'),
  sku: z.string().trim().min(1, 'Give the item a SKU.').max(100, 'At most 100 characters.'),
  unitOfMeasure: z
    .string({ required_error: 'Choose how it is measured.' })
    .min(1, 'Choose how it is measured.'),
  categoryId: z.string(),
  constructionType: z.nativeEnum(ConstructionType),
  costMethod: z.enum(['AVCO', 'FIFO']),
  // Optional: an emptied field is left off, not sent as zero.
  defaultUnitCost: z
    .number({ invalid_type_error: 'Enter a number.' })
    .min(0, 'Cannot be negative.')
    .optional(),
  // Only asked when editing. ARCHIVED is only ever the value it already had.
  status: z.enum(['ACTIVE', 'INACTIVE', 'ARCHIVED']),
})

type FormValues = z.infer<typeof schema>

/** What the form starts from: the item's own values, or the API's defaults for a new one. */
function defaultsOf(item: StockItem | undefined): Partial<FormValues> {
  if (!item) {
    return {
      name: '',
      sku: '',
      categoryId: NO_CATEGORY,
      constructionType: ConstructionType.Composite,
      costMethod: 'AVCO',
      status: 'ACTIVE',
    }
  }

  return {
    name: item.name,
    sku: item.sku,
    unitOfMeasure: item.unitOfMeasure,
    categoryId: item.category?.id ?? NO_CATEGORY,
    constructionType: item.constructionType,
    costMethod: item.costMethod,
    defaultUnitCost: item.defaultUnitCost,
    status: item.status,
  }
}

/**
 * Only what changed, against the item as it was read.
 *
 * Unchanged fields stay off the wire on purpose: the unit and how the item is
 * made are frozen once stock has moved, and sending them back unchanged would
 * have to be judged by the API as "no change" — leaving them off cannot fail.
 */
function changesOf(item: StockItem, values: FormValues): UpdateStockItemBody {
  const body: UpdateStockItemBody = { version: item.version }
  const name = values.name.trim()
  const sku = values.sku.trim()
  const categoryId = values.categoryId === NO_CATEGORY ? null : values.categoryId

  if (name !== item.name) body.name = name
  if (sku !== item.sku) body.sku = sku
  if (categoryId !== (item.category?.id ?? null)) body.categoryId = categoryId
  if (values.unitOfMeasure !== item.unitOfMeasure) body.unitOfMeasure = values.unitOfMeasure
  if (values.constructionType !== item.constructionType) {
    body.constructionType = values.constructionType
  }
  if (values.costMethod !== item.costMethod) body.costMethod = values.costMethod
  // An emptied cost is left as it was: there is no "no cost" to set it back to.
  if (values.defaultUnitCost !== undefined && values.defaultUnitCost !== item.defaultUnitCost) {
    body.defaultUnitCost = values.defaultUnitCost
  }
  if (values.status !== item.status && values.status !== 'ARCHIVED') body.status = values.status

  return body
}

interface StockItemFormDialogProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  onSaved: () => void
  /** The item to edit. Left off, the form creates a new ingredient. */
  item?: StockItem
}

/**
 * A new ingredient, or a change to any stock item.
 *
 * New: an ingredient is the only kind created here. The kind is fixed rather
 * than asked — an ingredient is what goes into what the business sells, and the
 * form is for that. With the kind settled, the catalogue link goes too — an
 * ingredient is not a product on the menu — and so does stock tracking: every
 * ingredient made here is counted (`STOCKED`). The unit has no default — guessing
 * "each" for something bought by the kilo would be wrong in a way nobody notices
 * until the first count. The rest default to the common case, the same defaults
 * the API applies.
 *
 * Edit: exactly the fields New asks, filled in, plus the status — so the two
 * forms read the same. Only what changed is sent, with the version the row was
 * read at; fields neither form shows (reorder level, safety buffer) are never
 * sent, so they keep whatever they hold. A product's name and SKU are its
 * catalogue's — the catalogue writes them over on its next change — so they are
 * shown but not edited here. The unit freezes once any stock has moved; the form
 * still offers it, and the API's refusal names the reason when that is what
 * stands in the way.
 *
 * Mounted fresh for each opening (the caller keys it), so nothing typed into an
 * abandoned form comes back the next time.
 */
export function StockItemFormDialog({
  isOpen,
  onOpenChange,
  onSaved,
  item,
}: StockItemFormDialogProps) {
  const { create, update } = useStockItemActions()
  const categoryList = useInventoryCategories()
  /* The units to measure in come from the service. An item's own unit stays
     offered even if it has since been switched off, or the select would go
     blank under it. */
  const { catalog: units } = useUnitCatalog()

  /* One idempotency key per distinct body. A retry of the same body after a
     timeout reuses the key, so the server replays its first answer instead of
     creating a second item. A changed body gets a new key — the server refuses a
     key reused for different content. */
  const [attempt, setAttempt] = useState<{ key: string; body: string } | null>(null)

  const { control, handleSubmit } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: defaultsOf(item),
  })

  const isEdit = Boolean(item)
  const unitGroupsForSelect =
    item && !units.has(item.unitOfMeasure)
      ? [
          ...unitOptionGroups(units),
          {
            label: 'No longer offered',
            options: [{ value: item.unitOfMeasure, label: item.unitOfMeasure }],
          },
        ]
      : unitOptionGroups(units)
  /* The active categories, plus the item's own when it is not among them — a
     category switched off since keeps its items, and the select would go blank
     under one it does not list. */
  const categoryOptions = [
    { value: NO_CATEGORY, label: 'No category' },
    ...categoryList.categories.map((category) => ({ value: category.id, label: category.name })),
    ...(item?.category &&
    !categoryList.categories.some((category) => category.id === item.category?.id)
      ? [{ value: item.category.id, label: `${item.category.name} (inactive)` }]
      : []),
  ]
  const isCatalogueOwned = Boolean(item?.productId)
  const kindLabel = item ? itemTypeLabels[item.stockItemType].toLowerCase() : 'ingredient'
  const mutation = isEdit ? update : create

  const submit = handleSubmit(async (values) => {
    if (item) {
      const body = changesOf(item, values)
      // Nothing but the version: nothing to save.
      if (Object.keys(body).length > 1) await update.mutateAsync({ id: item.id, body })
      onSaved()
      return
    }

    const body: CreateStockItemBody = {
      name: values.name.trim(),
      sku: values.sku.trim(),
      stockItemType: StockItemType.Ingredient,
      unitOfMeasure: values.unitOfMeasure,
      trackingType: 'STOCKED',
      constructionType: values.constructionType,
      costMethod: values.costMethod,
      // Reorder level and safety buffer are not asked here: the API starts both at 0.
    }

    // Left off when unset: the service rejects an empty id, and an absent one
    // already means "none".
    if (values.categoryId !== NO_CATEGORY) body.categoryId = values.categoryId
    if (values.defaultUnitCost !== undefined) body.defaultUnitCost = values.defaultUnitCost

    const serialised = JSON.stringify(body)
    const next =
      attempt?.body === serialised ? attempt : { key: crypto.randomUUID(), body: serialised }
    setAttempt(next)

    await create.mutateAsync({ body, idempotencyKey: next.key })
    onSaved()
  })

  const statusOptions = [
    { value: 'ACTIVE', label: itemStatusLabels.ACTIVE },
    { value: 'INACTIVE', label: itemStatusLabels.INACTIVE },
    // Shown only as where an archived item stands; choosing Active brings it back.
    ...(item?.status === 'ARCHIVED'
      ? [{ value: 'ARCHIVED', label: itemStatusLabels.ARCHIVED }]
      : []),
  ]

  return (
    <FormModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={item ? `Edit ${item.name}` : 'New ingredient'}
      description={item ? undefined : 'Coffee beans, milk, flour, syrup…'}
      submitLabel={isEdit ? 'Save changes' : 'Create ingredient'}
      onSubmit={submit}
      isBusy={mutation.isPending}
      error={mutation.error}
      errorFallback={
        isEdit ? `Could not save this ${kindLabel}.` : 'Could not create this ingredient.'
      }
      // Two sections of two-column fields: wide enough for the pairs to sit side
      // by side, and scrolling inside so Save stays in reach.
      dialogClassName="max-w-3xl"
      scroll="inside"
    >
      <FormSection title="Details">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextFieldControl
            control={control}
            name="name"
            label="Name"
            isRequired
            isDisabled={isCatalogueOwned}
            description={isCatalogueOwned ? 'Set in your catalogue.' : undefined}
            autoFocus={!isCatalogueOwned}
          />
          <TextFieldControl
            control={control}
            name="sku"
            label="SKU"
            isRequired
            isDisabled={isCatalogueOwned}
            description={isCatalogueOwned ? 'Set in your catalogue.' : 'Must be unique.'}
          />
          <SelectFieldControl
            control={control}
            name="categoryId"
            label="Category"
            options={categoryOptions}
            description={
              categoryList.error ? (
                <span className="text-danger flex flex-wrap items-center gap-x-1">
                  {errorMessage(categoryList.error, 'Could not load your categories.')}
                  <Button
                    variant="ghost"
                    size="sm"
                    onPress={categoryList.retry}
                    className="text-danger h-auto min-w-0 p-0 text-xs underline"
                  >
                    Try again
                  </Button>
                </span>
              ) : categoryList.isLoading ? (
                'Loading your categories…'
              ) : undefined
            }
          />
          {isEdit && (
            <SelectFieldControl
              control={control}
              name="status"
              label="Status"
              isRequired
              options={statusOptions}
              description={item?.status === 'ARCHIVED' ? 'Set to Active to restore.' : undefined}
            />
          )}
        </div>
      </FormSection>

      <FormSection title="Stock & cost" description="On-hand stock comes from receipts and counts.">
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectFieldControl
            control={control}
            name="unitOfMeasure"
            label="Base unit"
            isRequired
            groups={unitGroupsForSelect}
            description={isEdit ? 'Locked once stock moves.' : undefined}
          />
          {/* <SelectFieldControl
            control={control}
            name="constructionType"
            label="Made how"
            isRequired
            options={optionsOf(constructionLabels)}
            description={
              isEdit ? 'Locked once stock moves.' : 'Items made from others get a recipe.'
            }
          /> */}
          {/* <SelectFieldControl
            control={control}
            name="costMethod"
            label="Cost method"
            isRequired
            options={optionsOf(costMethodLabels)}
          /> */}
          <NumberFieldControl
            control={control}
            name="defaultUnitCost"
            label="Default cost"
            minValue={0}
            step={0.01}
            formatOptions={UNIT_COST_FORMAT}
            description="Per unit, until a receipt sets it."
          />
        </div>
      </FormSection>
    </FormModal>
  )
}
